import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { FadeIn } from "@/components/latir/Motion";
import { Button } from "@/components/latir/Ui";
import { useSEO } from "@/hooks/useSEO";

const ease = [0.16, 1, 0.3, 1];

export default function Home() {
  const { t } = useLang();

  useSEO({
    title: "Arteconciencia | Arte contemporáneo con impacto social en El Salvador",
    description:
      "Arteconciencia es una plataforma de arte contemporáneo en El Salvador que une arte, cultura y concientización a través de exposiciones, artistas y proyectos con impacto social.",
    canonical: "/",
  });

  // Versión post-muestra: a partir del 16 oct 2026 (El Salvador) la home
  // invita a revivir el recorrido digital en lugar de reservar visita.
  const exhibitionEnded = new Date() >= new Date("2026-10-16T00:00:00-06:00");

  return (
    <div data-testid="page-home">
      {/* ==================== HERO — Palacio Nacional ==================== */}
      <section
        data-testid="home-hero"
        className="relative w-full h-[88svh] min-h-[600px] lg:h-[90vh] overflow-hidden bg-paper"
      >
        {/* Fotografía protagonista */}
        <motion.img
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.8, ease }}
          src="/brand/palacio-nacional.webp"
          alt="Palacio Nacional de El Salvador"
          className="absolute inset-0 h-full w-full object-cover object-[center_35%]"
          data-testid="hero-image"
        />

        {/* Velo cálido horizontal — Vista 3 (lg, 1024–1279 px).
            Más denso y extendido para proteger el bloque editorial en
            este ancho intermedio donde el texto quedaba demasiado sobre
            la fotografía. Se restaura al comportamiento aprobado en xl+. */}
        <div
          aria-hidden
          className="absolute inset-0 hidden lg:block xl:hidden"
          style={{
            background:
              "linear-gradient(90deg, rgba(241,238,231,0.97) 0%, rgba(241,238,231,0.95) 30%, rgba(241,238,231,0.80) 48%, rgba(241,238,231,0.45) 62%, rgba(241,238,231,0.14) 76%, rgba(241,238,231,0) 86%)",
          }}
        />
        {/* Velo cálido horizontal — Vista 4 (xl+, ≥ 1280 px). Aprobado. */}
        <div
          aria-hidden
          className="absolute inset-0 hidden xl:block"
          style={{
            background:
              "linear-gradient(90deg, rgba(241,238,231,0.94) 0%, rgba(241,238,231,0.88) 22%, rgba(241,238,231,0.55) 36%, rgba(241,238,231,0.20) 50%, rgba(241,238,231,0.04) 62%, rgba(241,238,231,0) 72%)",
          }}
        />
        {/* Overlay móvil + tablet (< 1024 px): velo bottom-up denso donde vive
            el contenido, sin oscurecer la fachada del Palacio arriba. */}
        <div
          aria-hidden
          className="absolute inset-0 lg:hidden"
          style={{
            background:
              "linear-gradient(to top, rgba(241,238,231,0.98) 0%, rgba(241,238,231,0.95) 45%, rgba(241,238,231,0.62) 65%, rgba(241,238,231,0.18) 84%, rgba(241,238,231,0) 100%)",
          }}
        />
        {/* Vignette inferior muy leve para asentar los CTAs */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10"
        />

        {/* Contenido sobre la fotografía */}
        <div className="relative z-10 h-full flex flex-col justify-between px-6 sm:px-10 lg:px-16 pt-32 sm:pt-36 lg:pt-40 pb-10 sm:pb-14 lg:pb-16">
          {/* Ubicación (arriba, izquierda) — negro sobre veil horizontal en
              desktop (≥1024). En tablet/mobile, blanco con sombra sutil
              porque queda sobre la fachada del Palacio. */}
          {/* H1 de la home: la marca (SEO) — overline editorial */}
          <FadeIn delay={0.3} duration={0.9}>
            <h1
              className="font-ui uppercase tracking-[0.3em] lg:tracking-[0.34em] text-[0.72rem] sm:text-[0.8rem] lg:text-[0.84rem] font-semibold text-white lg:text-ink drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] lg:drop-shadow-none mb-3"
              data-testid="hero-brand"
            >
              Arteconciencia
            </h1>
          </FadeIn>
          <FadeIn delay={0.4} duration={0.9}>
            <span
              className="font-ui uppercase tracking-[0.24em] lg:tracking-[0.28em] text-[0.68rem] sm:text-[0.72rem] lg:text-[0.74rem] font-medium text-white lg:text-ink drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] lg:drop-shadow-none"
              data-testid="hero-location"
            >
              {t("home.location")}
            </span>
          </FadeIn>

          {/* Bloque principal (abajo, izquierda) */}
          <div className="max-w-3xl">
            {/* Logo original LATIR */}
            <motion.img
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.55, ease }}
              src="/brand/latir-logo-official.webp"
              alt="LATIR El placer de vivir — exposición de Arteconciencia en el Palacio Nacional de El Salvador"
              data-testid="hero-logo"
              className="w-[180px] sm:w-[280px] lg:w-[288px] xl:w-[320px] h-auto"
            />

            {/* Bloque editorial (frase + lede + sublede + CTAs)
                — ancho controlado y eje izquierdo compartido. */}
            <div className="mt-6 sm:mt-8 lg:mt-14 text-left">
              <div className="w-full max-w-[85vw] sm:max-w-[520px] lg:max-w-[420px] xl:max-w-[460px]">
                {/* Frase curatorial (el H1 de la página es la marca) */}
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.75, ease }}
                  className="font-ui italic uppercase tracking-[0.28em] sm:tracking-[0.32em] leading-[1.4] text-[0.78rem] sm:text-[1.02rem] lg:text-[1.02rem] xl:text-[1.12rem] text-latir-red"
                  data-testid="hero-phrase"
                >
                  “{t("home.phrase")}”
                </motion.p>

                {/* Lede */}
                <motion.p
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.95, ease }}
                  className="mt-3 sm:mt-7 font-editorial text-[0.98rem] sm:text-[1.22rem] lg:text-[1.20rem] xl:text-[1.32rem] leading-[1.35] sm:leading-[1.45] text-ink"
                  data-testid="hero-lede"
                >
                  {t(exhibitionEnded ? "home.hero.lede.post" : "home.hero.lede")}
                </motion.p>

                {/* Sublede — Arteconciencia · Fundarosa */}
                <motion.p
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 1.1, ease }}
                  className="mt-3 sm:mt-4 font-ui text-[0.86rem] sm:text-[0.92rem] tracking-[0.06em] sm:tracking-[0.08em] leading-[1.55] text-ink/80"
                  data-testid="hero-sublede"
                >
                  {t("home.hero.sublede")}
                </motion.p>
              </div>

              {/* CTAs — apilados sólo en mobile; horizontales, compactos y
                  alineados a la izquierda desde md (≥ 768 px). */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1.3, ease }}
                className="mt-5 sm:mt-8 lg:mt-10 flex flex-col md:flex-row md:flex-wrap items-start md:items-center gap-3 sm:gap-4"
              >
                <Button
                  to="/latir"
                  variant="primary"
                  arrow="right"
                  data-testid="hero-cta-explorar"
                  className="w-full md:w-auto whitespace-nowrap"
                >
                  {t("home.hero.explorar")}
                </Button>
                {exhibitionEnded ? (
                  <Button
                    to="/latir"
                    variant="outline"
                    arrow="right"
                    data-testid="hero-cta-revivir"
                    className="w-full md:w-auto whitespace-nowrap"
                  >
                    {t("home.hero.revivir")}
                  </Button>
                ) : (
                  <Button
                    to="/reservas"
                    variant="outline"
                    arrow="right"
                    data-testid="hero-cta-reservar"
                    className="w-full md:w-auto whitespace-nowrap"
                  >
                    {t("home.hero.reservar")}
                  </Button>
                )}
              </motion.div>

              {/* Microcopy discreto asociado al recorrido virtual */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.45, ease }}
                className="mt-3 sm:mt-4 font-ui uppercase tracking-[0.22em] text-[0.6rem] sm:text-[0.62rem] text-ink/55"
                data-testid="hero-cta-recorrido-sub"
              >
                {t("home.hero.explorar.sub")}
              </motion.p>
            </div>
          </div>
        </div>

        {/* Créditos foto (esquina inferior derecha) */}
        <FadeIn delay={1.3} duration={0.9}>
          <span
            className="absolute right-4 sm:right-8 lg:right-12 bottom-3 sm:bottom-4 z-10 font-ui uppercase tracking-[0.18em] text-[0.52rem] sm:text-[0.55rem] text-white/45 mix-blend-difference"
            data-testid="hero-credits"
          >
            Créditos: Erik Delula
          </span>
        </FadeIn>
      </section>

      {/* ==================== SOBRE LATIR ==================== */}
      <section
        className="px-6 sm:px-10 lg:px-16 py-24 sm:py-28 lg:py-32 max-w-5xl mx-auto"
        data-testid="home-about"
      >
        <FadeIn duration={0.8}>
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-latir-red/70" />
            <span className="font-ui uppercase tracking-[0.24em] text-[0.72rem] text-ink-mute">
              {t("home.about.eyebrow")}
            </span>
          </div>
        </FadeIn>

        <FadeIn delay={0.15} duration={0.9}>
          <p
            className="mt-8 font-editorial text-[1.15rem] sm:text-[1.3rem] lg:text-[1.4rem] leading-[1.7] text-ink"
            data-testid="home-about-text"
          >
            {t("home.intro")}
          </p>
        </FadeIn>
      </section>
    </div>
  );
}
